import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { styled, Theme } from '@mui/material/styles'
import { Dialog, Typography, Stack, Link } from '@mui/material'
import CoreHeader from './CoreHeader'
import CoreFooter from './CoreFooter'
import CoreTitle from './CoreTitle'
import Scrollbar from '../../control/Scrollbar'
import ImageGallery from '../../control/ImageGallery'
import useContent from '../../hooks/map/useContent'
import { menuMap } from '../../constant/Constant'

const NoImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/logo_no_image.png`
const AnnounceImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/logo_announce.png`

type InfoContentProps = {
    id: number
}

const InfoContent = (props: InfoContentProps) => {
    const { id } = props
    const { data, noDataMessage, isDataNotFound, callServiceGetContent } = useContent()

    const [titleName, setTitleName] = useState<string>('')
    const [openDialog, setOpenDialog] = useState(true)

    const { t } = useTranslation()
    const classes = useStyles()

    useEffect(() => {
        setTitleName(menuMap.filter((item: MapMenuType) => item.id === id)[0].name)
        callServiceGetContent(id)
    }, [])

    return (
        <Dialog
            fullScreen
            open={openDialog}
            onClose={() => {
                setOpenDialog(false)
            }}
        >
            <CoreHeader />

            <StyledContent>
                <div style={{ width: '100%', height: '100%' }}>
                    <Scrollbar scrollbarTrackBorderRadius={10} scrollbarThumbBorderRadius={10}>
                        <StyledContainer>
                            <CoreTitle
                                label={titleName}
                                date={data?.versionDateTime}
                                displayDate={isDataNotFound ? false : data?.versionDateTime ? true : false}
                            />
                            <StyledContent>
                                {isDataNotFound ? (
                                    <StyledDivNotFound>
                                        <Stack alignItems="center">
                                            <StyledImgAnnounce src={AnnounceImage} />
                                            <Typography>{noDataMessage}</Typography>
                                        </Stack>
                                    </StyledDivNotFound>
                                ) : (
                                    <>
                                        {data?.coverImageUrl ? (
                                            <StyledDivCover>
                                                <StyledImgCover src={data?.coverImageUrl} />
                                            </StyledDivCover>
                                        ) : (
                                            <StyledDivCoverNoImage>
                                                <Stack alignItems="center">
                                                    <StyledImgNoImage src={NoImage} />
                                                    <Typography>{t('LABEL.NO_IMAGE')}</Typography>
                                                </Stack>
                                            </StyledDivCoverNoImage>
                                        )}
                                        {noDataMessage ? (
                                            <StyledDivNotFound>
                                                <Stack alignItems="center">
                                                    <Typography>{noDataMessage}</Typography>
                                                </Stack>
                                            </StyledDivNotFound>
                                        ) : (
                                            <StyledDivInfo>
                                                {data?.body && (
                                                    <StyledDivSection>
                                                        <div dangerouslySetInnerHTML={{ __html: data?.body || '' }} />
                                                    </StyledDivSection>
                                                )}
                                                {data?.pictures && data?.pictures?.length > 0 && (
                                                    <StyledDivSection>
                                                        <StyledTypoSection>
                                                            {t('LABEL.MAP.CMS_CONTENT.PICTURE')}
                                                        </StyledTypoSection>
                                                        <div>
                                                            <ImageGallery
                                                                data={data?.pictures.map((url: string) => ({
                                                                    original: url,
                                                                }))}
                                                                showFullscreenButton={false}
                                                                showBullets={true}
                                                                gridSize={{ xs: 12 }}
                                                                autoPlay={false}
                                                                slideInterval={5000}
                                                                additionalClass={classes.imageGallery}
                                                                useFullScreen
                                                                fullScreenSize="md"
                                                                showNav={true}
                                                            />
                                                        </div>
                                                    </StyledDivSection>
                                                )}
                                                {data?.pdfLinks && data?.pdfLinks?.length > 0 && (
                                                    <StyledDivSection>
                                                        <StyledTypoSection>
                                                            {t('LABEL.MAP.CMS_CONTENT.PDF')}
                                                        </StyledTypoSection>
                                                        <div>
                                                            {data?.pdfLinks.map((file: PDFLinkType, index: number) => {
                                                                return (
                                                                    <StyledDivAttachmentItem key={`pdf-${index}`}>
                                                                        <StyledDivAttachmentImage>
                                                                            <a href={file.urlPath} target="_blank">
                                                                                <StyledIconImg src={file.icon} />
                                                                            </a>
                                                                        </StyledDivAttachmentImage>
                                                                        <StyledDivAttachmentName>
                                                                            <StyledLink
                                                                                href={file.urlPath}
                                                                                target="_blank"
                                                                            >
                                                                                {file.displayText}
                                                                            </StyledLink>
                                                                        </StyledDivAttachmentName>
                                                                    </StyledDivAttachmentItem>
                                                                )
                                                            })}
                                                        </div>
                                                    </StyledDivSection>
                                                )}
                                                {data?.externalLinks && data?.externalLinks?.length > 0 && (
                                                    <StyledDivSection>
                                                        <StyledTypoSection>
                                                            {t('LABEL.MAP.CMS_CONTENT.EXTERNAL_LINK')}
                                                        </StyledTypoSection>
                                                        <div>
                                                            <ul>
                                                                {data?.externalLinks.map(
                                                                    (file: ExternalLinkType, index: number) => {
                                                                        return (
                                                                            <StyledDivExternalLinkItem
                                                                                key={`extLink-${index}`}
                                                                            >
                                                                                <li>
                                                                                    <StyledLink
                                                                                        href={file.urlPath}
                                                                                        target="_blank"
                                                                                    >
                                                                                        {file.displayText}
                                                                                    </StyledLink>
                                                                                </li>
                                                                            </StyledDivExternalLinkItem>
                                                                        )
                                                                    },
                                                                )}
                                                            </ul>
                                                        </div>
                                                    </StyledDivSection>
                                                )}
                                            </StyledDivInfo>
                                        )}
                                    </>
                                )}
                            </StyledContent>
                        </StyledContainer>
                    </Scrollbar>
                </div>
            </StyledContent>

            <StyledFooter>
                <CoreFooter />
            </StyledFooter>
        </Dialog>
    )
}

export default InfoContent

const useStyles = makeStyles((theme: Theme) => ({
    imageGallery: {
        '& .image-gallery-content .image-gallery-slide .image-gallery-image': {
            maxHeight: '40vh',
        },
    },
}))

const StyledContainer = styled('div')(({ theme }) => ({
    padding: '30px 100px',
    [theme.breakpoints.down('sm')]: {
        padding: '30px 20px',
    },
}))

const StyledFooter = styled('div')(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    width: '100%',
}))

const StyledContent = styled('div')(({ theme }) => ({
    height: `calc(100% - 92px)`,
}))

const StyledDivCover = styled('div')(({ theme }) => ({
    margin: '20px 0px',
}))

// const StyledDivCover = styled('div')(({ theme }) => ({
//     margin: '20px 0px',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
// }))

const StyledDivCoverNoImage = styled('div')(({ theme }) => ({
    height: '300px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '5px',
    backgroundColor: `${theme.palette.getContrastText('bg-cover-no-image')}`,
}))

const StyledImgCover = styled('img')(({ theme }) => ({
    width: '100%',
}))

// const StyledImgCover = styled('img')(({ theme }) => ({
//     [theme.breakpoints.up('md')]: {
//         maxHeight: '350px',
//     },
//     [theme.breakpoints.down('md')]: {
//         width: '100%',
//     },
// }))

const StyledImgAnnounce = styled('img')(({ theme }) => ({
    width: '70%',
}))

const StyledImgNoImage = styled('img')(({ theme }) => ({
    height: '50%',
}))

const StyledDivNotFound = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '50px',
}))

const StyledDivInfo = styled('div')(({ theme }) => ({
    margin: '20px 0px',
}))

const StyledDivSection = styled('div')(({ theme }) => ({
    margin: '20px 0px',
}))

const StyledTypoSection = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    margin: '5px 0px',
}))

const StyledDivAttachmentItem = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    margin: '10px 0px',
    paddingLeft: '20px',
}))

const StyledDivAttachmentImage = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    height: '100%',
}))

const StyledDivAttachmentName = styled('div')(({ theme }) => ({
    paddingLeft: '20px',
}))

const StyledIconImg = styled('img')(({ theme, src }) => ({
    maxWidth: '50px',
}))

const StyledLink = styled(Link)(({ theme }) => ({
    color: `${theme.palette.primary.main} !important`,
}))

const StyledDivExternalLinkItem = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    margin: '10px 0px',
}))
